import { useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Eye, ArrowRight } from "@phosphor-icons/react";
import { FadeIn, Stagger, StaggerItem } from "../components/Motion";
import { WORKS } from "../data/site";

const FILTER_TABS = [
  "All",
  "Book Design",
  "Logo",
  "Visiting Card",
  "Packaging",
  "Web & App",
];

const CATEGORY_COLORS = {
  "Book Design":    { bg: "rgba(14,165,233,0.1)",  border: "rgba(14,165,233,0.3)",  text: "#0EA5E9" },
  "Logo":           { bg: "rgba(159,18,57,0.1)",   border: "rgba(159,18,57,0.3)",   text: "#9F1239" },
  "Visiting Card":  { bg: "rgba(180,83,9,0.1)",    border: "rgba(180,83,9,0.3)",    text: "#B45309" },
  "Packaging":      { bg: "rgba(37,99,235,0.1)",   border: "rgba(37,99,235,0.3)",   text: "#2563EB" },
  "Web & App":      { bg: "rgba(124,58,237,0.1)",  border: "rgba(124,58,237,0.3)",  text: "#7C3AED" },
  "Label Design":   { bg: "rgba(16,185,129,0.1)",  border: "rgba(16,185,129,0.3)",  text: "#10B981" },
  "Print":          { bg: "rgba(225,29,72,0.1)",   border: "rgba(225,29,72,0.3)",   text: "#E11D48" },
  "UI/UX":          { bg: "rgba(124,58,237,0.1)",  border: "rgba(124,58,237,0.3)",  text: "#7C3AED" },
  "Development":    { bg: "rgba(11,46,94,0.1)",    border: "rgba(11,46,94,0.3)",    text: "#0B2E5E" },
  "Branding":       { bg: "rgba(159,18,57,0.1)",   border: "rgba(159,18,57,0.3)",   text: "#9F1239" },
  "Product Design": { bg: "rgba(37,99,235,0.1)",   border: "rgba(37,99,235,0.3)",   text: "#2563EB" },
};

function TagChip({ tag }) {
  const style = CATEGORY_COLORS[tag] || {
    bg: "rgba(11,46,94,0.08)",
    border: "rgba(11,46,94,0.2)",
    text: "#0b2e5e",
  };
  return (
    <span
      className="text-[10px] tracking-[0.16em] uppercase font-medium px-2.5 py-1 rounded-full"
      style={{ background: style.bg, border: `1px solid ${style.border}`, color: style.text }}
    >
      {tag}
    </span>
  );
}

function WorkCard({ work, index }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20, scale: 0.97 }}
      transition={{ duration: 0.5, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className="group relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="relative rounded-3xl overflow-hidden bg-white border border-[#0b2e5e]/10 shadow-[0_4px_24px_-8px_rgba(11,46,94,0.18)] transition-all duration-500 group-hover:shadow-[0_20px_60px_-20px_rgba(11,46,94,0.35)] group-hover:-translate-y-1"
      >
        {/* Image Area */}
        <div className="relative overflow-hidden" style={{ aspectRatio: "4/3" }}>
          {work.img ? (
            <img
              src={work.img}
              alt={work.title}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
            />
          ) : (
            /* Placeholder when no image is provided */
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${work.accent}22 0%, ${work.accent}08 100%)`,
              }}
            >
              <span
                className="font-display text-7xl font-semibold tracking-tighter opacity-20"
                style={{ color: work.accent }}
              >
                {work.client.charAt(0)}
              </span>
            </div>
          )}

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0b2e5e]/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Hover CTA overlay */}
          <AnimatePresence>
            {hovered && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="absolute inset-0 flex items-center justify-center"
                style={{ background: "rgba(11,46,94,0.55)", backdropFilter: "blur(4px)" }}
              >
                <motion.div
                  initial={{ scale: 0.8, y: 10 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.8, y: 10 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="flex flex-col items-center gap-3"
                >
                  <div
                    className="h-14 w-14 rounded-full flex items-center justify-center"
                    style={{ background: work.accent, boxShadow: `0 8px 32px -8px ${work.accent}` }}
                  >
                    <Eye size={22} color="#fff" weight="bold" />
                  </div>
                  <span className="text-white font-mono-mini tracking-widest text-[10px]">
                    View Project
                  </span>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Year badge */}
          <div className="absolute top-4 right-4 text-[10px] font-mono tracking-[0.18em] uppercase px-2.5 py-1 rounded-full text-white"
            style={{ background: "rgba(15,23,42,0.55)", border: "1px solid rgba(255,255,255,0.2)" }}>
            {work.year}
          </div>

          {/* Featured dot */}
          {work.featured && (
            <div
              className="absolute top-4 left-4 h-2 w-2 rounded-full"
              style={{ background: work.accent, boxShadow: `0 0 10px ${work.accent}` }}
            />
          )}
        </div>

        {/* Card Body */}
        <div className="p-6">
          {/* Tags row */}
          <div className="flex flex-wrap gap-2 mb-4">
            {work.tags.map((tag) => (
              <TagChip key={tag} tag={tag} />
            ))}
          </div>

          <h3 className="font-display text-xl md:text-2xl leading-tight text-[#0b2e5e] group-hover:text-[#e11d48] transition-colors duration-300">
            {work.title}
          </h3>
          <p className="text-[#0b2e5e]/55 text-sm mt-1 font-medium">
            {work.client}
          </p>
          <p className="text-[#0b2e5e]/65 text-sm mt-3 leading-relaxed line-clamp-2">
            {work.description}
          </p>

          {/* Divider */}
          <div className="hr-grad mt-5 mb-4" />

          {/* Footer */}
          <div className="flex items-center justify-between">
            <span
              className="font-mono-mini"
              style={{ color: work.accent }}
            >
              § {work.id.toUpperCase().slice(0, 6)}
            </span>
            <div className="flex items-center gap-1.5 text-[#0b2e5e]/45 text-xs group-hover:text-[#e11d48] transition-colors duration-300">
              <span className="font-mono-mini">View</span>
              <ArrowUpRight size={12} weight="bold" />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Works() {
  const [activeFilter, setActiveFilter] = useState("All");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  const filtered =
    activeFilter === "All"
      ? WORKS
      : WORKS.filter((w) => w.tags.includes(activeFilter));

  const featuredCount = WORKS.filter((w) => w.featured).length;

  return (
    <div data-testid="works-page">
      {/* ── HERO ── */}
      <section ref={heroRef} className="relative overflow-hidden pt-36 pb-16">
        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="absolute inset-0 pointer-events-none"
        >
          <div className="absolute inset-0 grid-bg" />
          <div
            className="absolute -top-20 -right-20 w-[480px] h-[480px] rounded-full blur-3xl opacity-35"
            style={{ background: "radial-gradient(circle, #e11d48 0%, transparent 65%)" }}
          />
          <div
            className="absolute bottom-0 -left-20 w-[400px] h-[400px] rounded-full blur-3xl opacity-25"
            style={{ background: "radial-gradient(circle, #7c3aed 0%, transparent 65%)" }}
          />
          <div
            className="absolute top-40 left-1/3 w-[360px] h-[360px] rounded-full blur-3xl opacity-20"
            style={{ background: "radial-gradient(circle, #2563eb 0%, transparent 65%)" }}
          />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex items-center gap-6 mb-6"
            >
              <div className="h-[1px] w-10 bg-[#0b2e5e]/20" />
              <span className="issue-stripe">§ Portfolio — Selected Works</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-[64px] sm:text-[96px] lg:text-[120px] leading-[0.88] tracking-[-0.03em] font-semibold text-[#0b2e5e]"
            >
              Our{" "}
              <span className="font-serif-italic text-[#e11d48]">work.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-8 max-w-xl text-lg text-[#0b2e5e]/65 leading-relaxed"
            >
              {WORKS.length} projects. {featuredCount} featured picks. From brand identity
              and packaging to digital experiences — all crafted with obsessive attention to detail.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link to="/contact" className="btn-primary" data-testid="works-cta">
                Start a project <ArrowUpRight weight="bold" size={16} />
              </Link>
              <Link to="/services" className="btn-ghost" data-testid="works-services">
                Our services <ArrowUpRight weight="bold" size={16} />
              </Link>
            </motion.div>
          </FadeIn>
        </div>
      </section>

      {/* ── STICKY FILTER BAR ── */}
      <div className="sticky top-[72px] z-30 py-3">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="glass-strong rounded-full px-3 py-2 flex items-center gap-1.5 overflow-x-auto"
            style={{ scrollbarWidth: "none" }}
          >
            {FILTER_TABS.map((tab) => {
              const isActive = tab === activeFilter;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveFilter(tab)}
                  data-testid={`filter-${tab.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative flex-shrink-0 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    isActive
                      ? "text-white shadow-[0_4px_18px_-6px_rgba(225,29,72,0.6)]"
                      : "text-[#0b2e5e]/65 hover:text-[#0b2e5e] hover:bg-[#0b2e5e]/5"
                  }`}
                  style={isActive ? { background: "#e11d48" } : {}}
                >
                  {tab}
                  {tab !== "All" && (
                    <span className="ml-1.5 text-[10px] opacity-70">
                      ({WORKS.filter((w) => w.tags.includes(tab)).length})
                    </span>
                  )}
                  {tab === "All" && (
                    <span className="ml-1.5 text-[10px] opacity-70">({WORKS.length})</span>
                  )}
                </button>
              );
            })}
          </motion.div>
        </div>
      </div>

      {/* ── PROJECT GRID ── */}
      <section className="py-16 md:py-20" data-testid="works-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatePresence mode="popLayout">
            {filtered.length > 0 ? (
              <motion.div
                key={activeFilter}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
              >
                {filtered.map((work, i) => (
                  <WorkCard key={work.id} work={work} index={i} />
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-32 text-center"
              >
                <p className="font-display text-3xl text-[#0b2e5e]/30">No projects in this category yet.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ── FEATURED HIGHLIGHT BANNER ── */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div
              className="relative overflow-hidden rounded-3xl p-10 md:p-16"
              style={{
                background: "linear-gradient(135deg, #0b2e5e 0%, #1e40af 50%, #0b2e5e 100%)",
              }}
            >
              {/* Background orbs */}
              <div
                className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full blur-3xl opacity-30"
                style={{ background: "radial-gradient(circle, #e11d48 0%, transparent 65%)" }}
              />
              <div
                className="absolute bottom-0 left-1/3 w-[300px] h-[300px] rounded-full blur-3xl opacity-20"
                style={{ background: "radial-gradient(circle, #7c3aed 0%, transparent 65%)" }}
              />

              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                <div className="max-w-xl">
                  <span className="inline-flex items-center gap-2 text-white/60 font-mono-mini mb-4">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#e11d48]" />
                    Every project is a partnership
                  </span>
                  <h2 className="font-display text-4xl md:text-5xl text-white leading-tight tracking-tight">
                    Ready to create something{" "}
                    <span className="font-serif-italic text-[#fca5a5]">extraordinary?</span>
                  </h2>
                  <p className="mt-4 text-white/65 text-base md:text-lg leading-relaxed">
                    We take on a select number of projects at a time — so every brand gets our full
                    attention, creative energy, and zero compromise.
                  </p>
                </div>
                <Link
                  to="/contact"
                  className="flex-shrink-0 inline-flex items-center gap-3 bg-white text-[#0b2e5e] px-8 py-4 rounded-full font-medium text-base hover:bg-[#faf6ef] transition-all duration-300 shadow-[0_8px_32px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.4)] hover:-translate-y-0.5"
                  data-testid="works-bottom-cta"
                >
                  Let's talk <ArrowRight weight="bold" size={16} />
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
